// Safe Spot shipping protection: keeps one $5 protection line in the cart
// unless the customer switches it off. Updates the cart drawer in place.
import { morphSection } from '@theme/section-renderer';

const PROT_ID = 48814799356123;
const ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
let busy = false;

const sectionIds = () =>
  [...new Set([...document.querySelectorAll('cart-items-component')].map((el) => el.dataset.sectionId).filter(Boolean))];

const getCart = () => fetch(ROOT + 'cart.js', { headers: { Accept: 'application/json' } }).then((r) => r.json());

const summarize = (cart) => {
  let others = 0;
  let protQty = 0;
  for (const item of cart.items) {
    if (item.variant_id === PROT_ID) protQty += item.quantity;
    else others += item.quantity;
  }
  return { others, protQty, optedOut: cart.attributes && cart.attributes._ss_protection === 'off' };
};

async function update(payload) {
  if (busy) return;
  busy = true;
  document.querySelectorAll('[data-ss-prot]').forEach((el) => el.classList.add('is-busy'));
  try {
    const ids = sectionIds();
    const body = { ...payload };
    if (ids.length) {
      body.sections = ids.join(',');
      body.sections_url = window.location.pathname;
    }
    const res = await fetch(ROOT + 'cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    });
    const cart = await res.json();
    if (cart.sections) {
      for (const id of ids) {
        if (cart.sections[id]) morphSection(id, cart.sections[id]);
      }
    }
    const icon = document.querySelector('cart-icon');
    if (icon && typeof icon.renderCartBubble === 'function') icon.renderCartBubble(cart.item_count, false, false);
  } catch (e) {
    console.error('[ss-protection]', e);
  } finally {
    busy = false;
    document.querySelectorAll('[data-ss-prot]').forEach((el) => el.classList.remove('is-busy'));
  }
}

async function sync() {
  if (busy) return;
  const cart = await getCart();
  const { others, protQty, optedOut } = summarize(cart);
  if (others === 0 && protQty > 0) return update({ updates: { [PROT_ID]: 0 } });
  if (others > 0 && protQty > 1) return update({ updates: { [PROT_ID]: 1 } });
  if (others > 0 && protQty === 0 && !optedOut) return update({ updates: { [PROT_ID]: 1 } });
}

let timer;

async function afterAdd() {
  if (busy) {
    timer = setTimeout(afterAdd, 300);
    return;
  }
  const cart = await getCart();
  const { others, protQty, optedOut } = summarize(cart);
  let want = protQty;
  if (others === 0) want = 0;
  else if (protQty > 1) want = 1;
  else if (protQty === 0 && !optedOut) want = 1;
  await update({ updates: { [PROT_ID]: want } });
  const drawer = document.querySelector('cart-drawer-component');
  const dialog = drawer && drawer.querySelector('dialog');
  if (drawer && dialog && !dialog.open && typeof drawer.open === 'function') drawer.open();
}
const scheduleSync = (delay = 400) => {
  clearTimeout(timer);
  timer = setTimeout(sync, delay);
};

if (!window.__ssProtection) {
  window.__ssProtection = true;

  document.addEventListener('change', (event) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement) || !input.matches('[data-ss-prot-toggle]')) return;
    update({
      updates: { [PROT_ID]: input.checked ? 1 : 0 },
      attributes: { _ss_protection: input.checked ? 'on' : 'off' },
    });
  });

  // After anything is changed in the cart by the theme, keep protection in sync.
  document.addEventListener('cart:update', (event) => {
    if (event.detail && event.detail.data && event.detail.data.source === 'ss-protection') return;
    scheduleSync();
  });

  // Kaching adds to cart with its own fetch and doesn't tell the theme.
  // Catch every /cart/add, then refresh the cart drawer, sync protection and open the drawer.
  const nativeFetch = window.fetch.bind(window);
  window.fetch = async (...args) => {
    const res = await nativeFetch(...args);
    try {
      const target = args[0];
      const url = typeof target === 'string' ? target : (target && target.url) || '';
      if (/\/cart\/add(\.js)?(\?|$)/.test(url) && res.ok) {
        clearTimeout(timer);
        timer = setTimeout(afterAdd, 150);
      }
    } catch (e) {
      /* ignore */
    }
    return res;
  };

  // Fallback: when the cart drawer opens, make sure it's in sync.
  const watchDrawer = () => {
    const dialog = document.querySelector('cart-drawer-component dialog');
    if (!dialog) return;
    new MutationObserver(() => {
      if (dialog.open) scheduleSync(0);
    }).observe(dialog, { attributes: true, attributeFilter: ['open'] });
  };
  watchDrawer();

  // On the cart page, sync on load.
  if (/\/cart\/?$/.test(window.location.pathname)) scheduleSync(0);
}
